import { useEffect, useRef } from 'react';
import { Heading, Text, Button, Slider } from 'frosted-ui';
import { Rise } from '../animation';
import { useIsMobile } from '../../hooks/useIsMobile';

interface RevenueInputScreenProps {
  revenue: number;
  onRevenueChange: (value: number) => void;
  /** Called with the settled revenue (a preset's target, even mid-glide). */
  onNext: (revenue: number) => void;
}

const PRESETS = [500, 2000, 10000, 50000];

// Slider works in 0–1000 internal "positions". Revenue maps non-linearly so the
// lower end of the scale (where most creators sit) gets more pixel real estate.
const POSITION_MIN = 0;
const POSITION_MAX = 1000;
const POSITION_STEP = 1;

// Anchors: 0% → $0, 33% → $1k, 66% → $15k, 100% → $100k.
const ANCHORS: Array<{ position: number; revenue: number }> = [
  { position: 0, revenue: 0 },
  { position: 333, revenue: 1000 },
  { position: 666, revenue: 15000 },
  { position: 1000, revenue: 100000 },
];

function rawPositionToRevenue(position: number): number {
  for (let i = 0; i < ANCHORS.length - 1; i++) {
    const lo = ANCHORS[i];
    const hi = ANCHORS[i + 1];
    if (position <= hi.position) {
      const t = (position - lo.position) / (hi.position - lo.position);
      return lo.revenue + t * (hi.revenue - lo.revenue);
    }
  }
  return ANCHORS[ANCHORS.length - 1].revenue;
}

function positionToRevenue(position: number): number {
  return roundRevenue(rawPositionToRevenue(position));
}

function revenueToPosition(revenue: number): number {
  for (let i = 0; i < ANCHORS.length - 1; i++) {
    const lo = ANCHORS[i];
    const hi = ANCHORS[i + 1];
    if (revenue <= hi.revenue) {
      const t = (revenue - lo.revenue) / (hi.revenue - lo.revenue);
      return Math.round(lo.position + t * (hi.position - lo.position));
    }
  }
  return ANCHORS[ANCHORS.length - 1].position;
}

// Snap dragged revenue to clean dollar values per segment so the displayed
// number doesn't read as $1,237 mid-drag.
function roundRevenue(value: number): number {
  if (value < 1000) return Math.round(value / 50) * 50;
  if (value < 15000) return Math.round(value / 250) * 250;
  return Math.round(value / 1000) * 1000;
}

const formatCurrency = (value: number) =>
  `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export default function RevenueInputScreen({
  revenue,
  onRevenueChange,
  onNext,
}: RevenueInputScreenProps) {
  const animationFrameRef = useRef<number | null>(null);
  // Where a preset's glide is headed, so moving on mid-glide keeps the preset.
  const targetRef = useRef<number | null>(null);
  const isMobile = useIsMobile();

  const cancelAnimation = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    targetRef.current = null;
  };

  useEffect(() => cancelAnimation, []);

  const animateRevenueTo = (target: number) => {
    cancelAnimation();
    const start = revenue;
    if (start === target) return;
    targetRef.current = target;

    // Animate position linearly so the slider thumb moves at uniform visual speed
    // regardless of the non-linear revenue curve.
    const startPosition = revenueToPosition(start);
    const targetPosition = revenueToPosition(target);

    const startTime = performance.now();
    const duration = 450;

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);

      if (t === 1) {
        onRevenueChange(target);
        animationFrameRef.current = null;
        targetRef.current = null;
        return;
      }

      const currentPosition = startPosition + (targetPosition - startPosition) * eased;
      // Use raw position-to-revenue mapping (no segment snapping) so each frame
      // produces a finely-distinct value, keeping the thumb glide smooth.
      const currentRevenue = rawPositionToRevenue(currentPosition);
      onRevenueChange(Math.round(currentRevenue));

      animationFrameRef.current = requestAnimationFrame(tick);
    };
    animationFrameRef.current = requestAnimationFrame(tick);
  };
  const handleNext = () => {
    const settled = targetRef.current ?? revenue;
    cancelAnimation();
    if (settled !== revenue) onRevenueChange(settled);
    onNext(settled);
  };

  return (
    <div
      className="vara2-step-in"
      style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}
    >
      <div
        className="vara2-glow vara2-halo"
        style={{ top: -120, right: -120, animationDelay: '120ms' }}
      />

      <div className="vara2-onboarding-stack" style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <Rise delay={0}>
          <Text
            size="1"
            weight="medium"
            color="gray"
            style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}
          >
            Welcome to Paywall Lab
          </Text>
        </Rise>

        <Rise delay={80}>
          <Heading size={isMobile ? '6' : '8'} style={{ marginBottom: 'var(--space-2)' }}>
            First, a quick number.
          </Heading>
          <Text size="3" color="gray">
            Roughly how much do you earn from your Whop products in a typical month? We'll show you
            what's at stake.
          </Text>
        </Rise>

        <Rise delay={220} style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <div
            className="vara2-onboarding-card"
            style={{
              backgroundColor: 'var(--gray-a3)',
              border: '1px solid var(--gray-a5)',
              borderRadius: 'var(--radius-4)',
              padding: 'var(--space-5)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-4)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
              }}
            >
              <Text size="3" color="gray">
                Monthly revenue
              </Text>
              <Heading size={isMobile ? '5' : '7'} className="vara2-tabular">
                {formatCurrency(revenue)}
              </Heading>
            </div>

            <Slider
              color="iris"
              value={[revenueToPosition(revenue)]}
              min={POSITION_MIN}
              max={POSITION_MAX}
              step={POSITION_STEP}
              onValueChange={(values) => {
                cancelAnimation();
                onRevenueChange(positionToRevenue(values[0] ?? 0));
              }}
            />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {PRESETS.map((preset) => {
                const active = preset === revenue;
                return (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => animateRevenueTo(preset)}
                    className="vara2-chip"
                    style={{
                      cursor: 'pointer',
                      minHeight: isMobile ? 44 : undefined,
                      padding: '6px 14px',
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 500,
                      border: `1px solid ${active ? 'var(--iris-a8)' : 'var(--gray-a6)'}`,
                      backgroundColor: active ? 'var(--iris-a3)' : 'transparent',
                      color: active ? 'var(--iris-11)' : 'var(--gray-12)',
                    }}
                  >
                    {formatCurrency(preset)}/mo
                  </button>
                );
              })}
            </div>

            <Text size="1" color="gray">
              Your number stays on your screen. We use it only to personalize this estimate.
            </Text>
          </div>
        </Rise>

        <Rise delay={380}>
          <div className="vara2-sheen-host" style={{ borderRadius: 'var(--radius-3)' }}>
            <Button
              variant="solid"
              color="iris"
              size="4"
              onClick={handleNext}
              className="vara2-cta"
              style={{ width: '100%' }}
            >
              Show me what's at stake →
            </Button>
          </div>
        </Rise>
      </div>
    </div>
  );
}
